import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Box,
  DialogActions,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  FormHelperText,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FormattedMessage } from "react-intl";
import { Company } from "../../../../../../types/companies-managment/company";
import { useUsersForAutocomplete } from "../../../../../../hooks/query-hooks/useUsers";
import { useDebounce } from "../../../../../../hooks";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { orderStatusOptions } from "../../order/orders/mockData";

export type VacancyFiltersType = {
  id: string;
  company_id: string;
  from: string;
  to: string;
  duration: string;
  status: string;
};

export type FiltersModalProps = {
  open: boolean;
  handleClose: () => void;
  onApplyFilters: (filters: VacancyFiltersType) => void;
  onClearFilters: () => void;
};

const FiltersModal = ({
  open,
  handleClose,
  onApplyFilters,
  onClearFilters,
}: FiltersModalProps) => {
  const [id, setId] = useState("");
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [duration, setDuration] = useState("");
  const [status, setStatus] = useState("");

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const [companySearchTerm, setCompanySearchTerm] = useState("");

  const debouncedCompanySearchTerm = useDebounce(companySearchTerm, 500);

  const { items: companies = [], error: companiesError } =
    useUsersForAutocomplete("COMPANY", 20, debouncedCompanySearchTerm);

  const handleApply = () => {
    onApplyFilters({
      id: id,
      company_id: selectedCompany?.id?.toString() || "",
      from: fromDate ? fromDate.toISOString().split("T")[0] : "",
      to: toDate ? toDate.toISOString().split("T")[0] : "",
      duration: duration,
      status: status,
    });
    handleClose();
  };

  const durationOptions = [
    { value: "7", label: "7 days" },
    { value: "30", label: "30 days" },
    { value: "90", label: "90 days" },
    { value: "180", label: "180 days" },
    { value: "365", label: "1 year" },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: 500 }}>
          <FormattedMessage id="Filters" />
        </DialogTitle>
        <Divider sx={{ marginInline: "1rem" }} />
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  label={<FormattedMessage id="ID" />}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  value={selectedCompany}
                  onChange={(event, newValue) => {
                    setSelectedCompany(newValue);
                  }}
                  options={companies}
                  getOptionLabel={(option: Company) =>
                    option.name || option.user_name || ""
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<FormattedMessage id="Select company" />}
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                    />
                  )}
                  noOptionsText="No companies found"
                />
                {companiesError && (
                  <FormHelperText error>
                    Error loading companies. Please try again.
                  </FormHelperText>
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2} marginTop=".5rem">
              <Grid item xs={12} sm={6}>
                <DatePicker
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      label: <FormattedMessage id="From Date" />,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      label: <FormattedMessage id="To Date" />,
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} marginTop=".5rem">
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>
                    <FormattedMessage id="Duration" />
                  </InputLabel>
                  <Select
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    label={<FormattedMessage id="Duration" />}
                  >
                    <MenuItem value="">
                      <FormattedMessage id="All" />
                    </MenuItem>
                    {durationOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>
                    <FormattedMessage id="Status" />
                  </InputLabel>
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    label={<FormattedMessage id="Status" />}
                  >
                    <MenuItem value="">
                      <FormattedMessage id="All" />
                    </MenuItem>
                    {orderStatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <FormattedMessage id={option.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClose}
            sx={{ padding: ".3rem 2rem" }}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            color="primary"
            sx={{ padding: ".3rem 2rem" }}
          >
            <FormattedMessage id="Apply" />
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default FiltersModal;
